import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InventorySourcePreference } from 'src/app/core/infrastructure/enums/inventory-source-preference.enum';
import { Company } from 'src/app/core/models/company';
import { AdRecommendAction, AdRecommendationDisplay, AD_RECOMMENDATIONS, InventoryRow } from './product-ad-inventory-analysis-modal.constant';
import { ProductAds } from 'src/app/core/models/product-ads';
import { InventoryStatus } from 'src/app/core/infrastructure/enums/meta-data-field.enum';

export interface AdModalItem {
  sku?: string;
  inventorySourcePreference?: string;
  [key: string]: unknown;
}


@Component({
  selector: 'app-product-ad-inventory-analysis-modal',
  templateUrl: './product-ad-inventory-analysis-modal.component.html',
  styleUrls: ['./product-ad-inventory-analysis-modal.component.scss'],
})
export class ProductAdInventoryAnalysisModalComponent {

  _productAd: ProductAds;
  @Input() set productAd(item: ProductAds) {
    this._productAd = item;
    this.buildInventoryRows();
  }

  get productAd(): ProductAds {
    return this._productAd;
  }

  _company: Partial<Company> = {};
  @Input() set company(c: Partial<Company>) {
    this._company = c || {};
    this.buildInventoryRows();
  }
  
  get company(): Partial<Company> {
    return this._company;
  }

  // Section 1: Inventory Available
  inventoryRows: InventoryRow[] = [];

  private buildInventoryRows() {
    if (!this._productAd) return;
    
    const pref = this._productAd.inventorySourcePreference;
    const isAwdActive = Boolean(this._productAd.isUsingAwdInventoryQty);

    const showFbm = pref === InventorySourcePreference.FBM || pref === InventorySourcePreference.FBAPlusFBM;
    const showFba = pref === InventorySourcePreference.FBA || pref === InventorySourcePreference.FBAPlusFBM;
    const showAwd = isAwdActive;

    const infoParts: string[] = [];

    if (showFba) {
      infoParts.push(`FBA (${(this.productAd.inboundShipped || 0) + (this.productAd.inboundReceiving || 0)})`);
    }

    if (showAwd) {
      infoParts.push(`AWD (${(this.productAd.inboundShippedAwd || 0) + (this.productAd.inboundReceivingAwd || 0)})`);
    }

    const inboundInfo = infoParts.length > 0 ? infoParts.join(' + ') : undefined;

    this.inventoryRows = [
      {
        field: 'onHand',
        label: 'On Hand FBA',
        value: this.productAd.onHand || 0,
        hidden: !showFba,
      },
      {
        field: 'onHandFbm',
        label: 'On Hand FBM',
        value: this.productAd.onHandFbm || 0,
        hidden: !showFbm,
      },
      {
        label: 'FC Transfer',
        value: this.productAd.inboundFcTransfer || 0,
      },
      {
        field: 'awdInventoryQty',
        label: 'AWD',
        value: showAwd ? this.productAd.awdInventoryQty || 0 : 0,
        hidden: !showAwd,
      },
      {
        field: 'inbound',
        label: 'Inbound',
        value: this.productAd.inbound || 0,
        info: inboundInfo,
      },
      {
        field: 'inventoryAvailable',
        label: 'Total',
        value: this.productAd.inventoryAvailable || 0,
        isTotalRow: true,
      },
    ];
  }

  // Mock data for Section 2: Logic Applied

  readonly AdRecommendAction = AdRecommendAction;

  get sku(): string {
    return this.productAd?.sku || '';
  }

  get inventoryStatus(): string | undefined {
    return this.productAd.inventoryStatus || this.productAd.adStockOutStatus;
  }

  get minThreshold(): number {
    return this.company?.minDaysUntilStockOutRisk ?? 7;
  }

  get maxThreshold(): number {
    return this.company?.maxDaysUntilStockOutRisk ?? 30;
  }

  get isAtRisk(): boolean {
    return !!this.inventoryStatus && this.inventoryStatus !== InventoryStatus.WELL_STOCK;
  }

  get healthStatusText(): string {
    switch (this.inventoryStatus) {
      case InventoryStatus.OUT_OF_STOCK: return 'Out of Stock';
      case InventoryStatus.HIGH_RISK: return 'High Risk';
      case InventoryStatus.MODERATE_RISK: return 'Moderate Risk';
      case InventoryStatus.WELL_STOCK: return 'Well Stocked';
      default: return '';
    }
  }

  get healthChipClass(): string {
    switch (this.inventoryStatus) {
      case InventoryStatus.OUT_OF_STOCK: return 'chip-oos';
      case InventoryStatus.HIGH_RISK: return 'chip-high';
      case InventoryStatus.MODERATE_RISK: return 'chip-moderate';
      case InventoryStatus.WELL_STOCK: return 'chip-well';
      default: return '';
    }
  }

  get healthAlertClass(): string {
    switch (this.inventoryStatus) {
      case InventoryStatus.OUT_OF_STOCK: return 'alert-oos';
      case InventoryStatus.HIGH_RISK: return 'alert-high';
      case InventoryStatus.MODERATE_RISK: return 'alert-moderate';
      default: return 'alert-well';
    }
  }

  get active(): boolean {
    return this.productAd.active;
  }

  isManagedBySystem(): boolean{
    return this.productAd.managedBySystem;
  }

  get recommendation(): AdRecommendationDisplay {
    if (this.active && this.isAtRisk) {
      return AD_RECOMMENDATIONS.PAUSE;
    }
    if (!this.active && !this.isAtRisk) {
      return AD_RECOMMENDATIONS.ENABLE;
    }
    if (!this.active && this.isAtRisk) {
      return {
        ...AD_RECOMMENDATIONS.NONE_PAUSED,
        message: AD_RECOMMENDATIONS.NONE_PAUSED.message.replace('{status}', this.healthStatusText),
      };
    }
    return AD_RECOMMENDATIONS.NONE_ACTIVE;
  }

  constructor(public activeModal: NgbActiveModal) {}

  onAction() {
    this.activeModal.close(this.recommendation.action);
  }

  onCancel() {
    this.activeModal.dismiss('CANCEL');
  }
}